import { AutoAwesome } from '@mui/icons-material';
import { Box, Button, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserCustomization } from '~/backend/types';
import AvailableModels from '~/components/ModelSelection/AvailableModels';
import CustomizationSelection from '~/components/ModelSelection/CustomizationSelection';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import { Modality } from '~/domain/dicom/modality';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';

type ImageTranslationModelSelectionProps = {
    modelCustomizations?: UserCustomization<unknown>[];
    availableModels?: PredictionModel[];
    modality?: Modality;
    disabled?: boolean;
    isLoadingModels?: boolean;
    isSubmitting?: boolean;
    showPseudonymization: boolean;
    onSubmit: (model: PredictionModel, selectedCustomization?: string) => void;
};

export default function ImageTranslationModelSelection({
    availableModels = [],
    modelCustomizations = [],
    disabled = false,
    isLoadingModels,
    isSubmitting,
    showPseudonymization,
    onSubmit,
}: ImageTranslationModelSelectionProps) {
    const [selectedModel, setSelectedModel] = useState<PredictionModel>();
    const [selectedCustomization, setSelectedCustomization] =
        useState<string>();

    const customizations = useMemo(() => {
        if (!selectedModel || !modelCustomizations) return [];
        const selectedModelCustomizations =
            selectedModel?.customizations?.filter(
                (customization) => customization.name !== selectedModel.name
            );
        return selectedModelCustomizations || [];
    }, [selectedModel, modelCustomizations]);

    const hasCustomizations = customizations.length > 0;

    return (
        <>
            <Box
                display="grid"
                gridTemplateRows="240px auto"
                gridTemplateColumns={hasCustomizations ? '60% 1fr' : '100%'}
                gap={hasCustomizations ? 1 : 0}
            >
                <AvailableModels
                    isLoading={isLoadingModels}
                    models={availableModels}
                    selectedModel={selectedModel}
                    setSelectedModel={setSelectedModel}
                />

                {hasCustomizations && (
                    <CustomizationSelection
                        customizations={customizations}
                        selectedCustomization={selectedCustomization}
                        setSelectedCustomization={setSelectedCustomization}
                    />
                )}
            </Box>
            <Box display="flex" justifyContent="flex-end">
                <Stack gap={0.5}>
                    {showPseudonymization && <PseudonymizeCheckbox />}
                    <Button
                        onClick={() =>
                            selectedModel &&
                            onSubmit(selectedModel, selectedCustomization)
                        }
                        variant="outlined"
                        endIcon={<AutoAwesome />}
                        disabled={!selectedModel || disabled || isSubmitting}
                        sx={{ width: 160 }}
                    >
                        <FormattedMessage id="start" />
                    </Button>
                </Stack>
            </Box>
        </>
    );
}
